import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { scrollIntoView } from '@theme/scrolling';
import { StandardEvents } from '@shopify/events';

/**
 * Keeps variant-specific PDP accordion content synchronized with the selected variant.
 */
class PdpProductAccordions extends Component {
  #scrollFrame;

  connectedCallback() {
    super.connectedCallback();
    this.#eventTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#bindScrollAccordions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#eventTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.querySelectorAll('[data-scroll-content-on-open]').forEach((accordion) => {
      accordion.removeEventListener('toggle', this.#handleAccordionToggle);
    });
    window.cancelAnimationFrame(this.#scrollFrame);
  }

  get #eventTarget() {
    return this.closest('[id*="ProductInformation-"]');
  }

  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const updatedAccordions = detail.html.querySelector('pdp-product-accordions');
        if (!(updatedAccordions instanceof HTMLElement)) return;

        this.toggleAttribute('hidden', updatedAccordions.hidden);
        morph(this, updatedAccordions, { childrenOnly: true });
        this.#bindScrollAccordions();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[pdp-product-accordions] Variant update failed', error);
      });
  };

  #bindScrollAccordions() {
    this.querySelectorAll('[data-scroll-content-on-open]').forEach((accordion) => {
      accordion.addEventListener('toggle', this.#handleAccordionToggle);
    });
  }

  #handleAccordionToggle = (event) => {
    const accordion = event.currentTarget;
    window.cancelAnimationFrame(this.#scrollFrame);

    if (
      !(accordion instanceof HTMLDetailsElement) ||
      !accordion.open ||
      !accordion.hasAttribute('data-scroll-content-on-open') ||
      !window.matchMedia('(min-width: 990px)').matches
    ) {
      return;
    }

    this.#scrollFrame = window.requestAnimationFrame(() => {
      if (!accordion.isConnected || !accordion.open) return;

      const desktopScrollContainer = accordion.closest('.product-details.sticky-content--desktop');
      const desktopPageWrapper = accordion.closest('.page-wrapper');
      if (!(desktopScrollContainer instanceof HTMLElement) || !(desktopPageWrapper instanceof HTMLElement)) {
        return;
      }

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion) {
        scrollIntoView(accordion, {
          ancestor: desktopScrollContainer,
          behavior: 'auto',
          block: 'start',
        });
        return;
      }

      const scrollTrackingEndsAt = performance.now() + 350;
      const trackExpandingAccordion = () => {
        if (!accordion.isConnected || !accordion.open) return;

        scrollIntoView(accordion, {
          ancestor: desktopScrollContainer,
          behavior: 'instant',
          block: 'start',
        });

        const documentList = accordion.querySelector('.pdp-product-document-links');
        if (documentList instanceof HTMLElement) {
          const documentRect = documentList.getBoundingClientRect();
          const wrapperRect = desktopPageWrapper.getBoundingClientRect();
          const visibleBottom = Math.min(wrapperRect.bottom, window.innerHeight);
          const overflow = documentRect.bottom + 16 - visibleBottom;

          if (overflow > 0) {
            desktopPageWrapper.scrollTo({
              top: desktopPageWrapper.scrollTop + Math.min(overflow, 24),
              behavior: 'instant',
            });
          }
        }

        if (performance.now() < scrollTrackingEndsAt) {
          this.#scrollFrame = window.requestAnimationFrame(trackExpandingAccordion);
        }
      };

      trackExpandingAccordion();
    });
  };
}

if (!customElements.get('pdp-product-accordions')) {
  customElements.define('pdp-product-accordions', PdpProductAccordions);
}
